import React, { useEffect, useMemo } from "react";
import StyledText from "../../components/StyledComponents/StyledText";
import { CardContainer } from "./MemberDirectorySettings";
import StyledInput from "../../components/StyledComponents/StyledInput";
import { Button } from "../../components/form/Button";
import CrossIcon from "../../assets/icons/CrossIcon";
import SaveIcon from "../../assets/icons/SaveIcon";
import { useSelector } from "react-redux";
import { selectAuth } from "../auth/authSlice";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { useToast } from "../../components/toast/ToastContext";
import FormikInputField from "../../components/Formik/FormikInputField";
import { useBulkUpdateFieldsMutation } from "../../api/searchQueryApiSlice";

interface ChangeLoginEmailValues {
  currentEmail: string;
  newEmail: string;
}

const ChangeLoginEmail = () => {
  const auth = useSelector(selectAuth);
  const showToast = useToast();
  const [updateFields, { isLoading: isUpdating }] =
    useBulkUpdateFieldsMutation();

  const profileEmail = auth.email || undefined;

  const initialValues = useMemo<ChangeLoginEmailValues>(
    () => ({
      currentEmail: profileEmail || "",
      newEmail: "",
    }),
    [profileEmail],
  );

  const validationSchema = Yup.object({
    currentEmail: Yup.string().required("Current email is required"),
    newEmail: Yup.string()
      .required("New email is required")
      .email("Please enter a valid email address"),
  });

  const handleSubmit = async (
    values: ChangeLoginEmailValues,
    {
      setSubmitting,
      resetForm,
      setFieldError,
    }: FormikHelpers<ChangeLoginEmailValues>,
  ) => {
    const payload = {
      "fields": [
        {
            "fieldAccessKey": "contact_email",
            "value": values.newEmail
        },
      ],
      "scope": "contact",
      "recordIds": [
          auth?.userId
      ],
    };

    try {
      if (!auth || !auth.userId) {
        showToast("Error", "User not authenticated", "error");
        return;
      }
      await updateFields(payload).unwrap();

      resetForm();
      showToast("Success", "Email Updated successfully", "success");
    } catch (err: any) {
      const errorMessage =
        err?.data?.message || err?.error || "Failed to update email";

      showToast("Error", errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  // console.log('atuth', auth);

  return (
    <CardContainer>
      <div className="border-bottom mb-3 pb-3">
        <StyledText
          fontSize="15px"
          fontWeight={700}
          textColor={"var(--app-primary-color)"}
        >
          Change Login Email
        </StyledText>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          errors,
          touched,
          isSubmitting,
          handleChange,
          handleReset,
          dirty,
          isValid,
        }) => (
          <Form>
            <StyledText
              className="mt-4"
              fontSize="14px"
              fontWeight={600}
              textColor={"var(--app-secondary-color)"}
              style={{ marginBottom: "10px" }}
            >
              Current Email
            </StyledText>
            <FormikInputField
              name="currentEmail"
              type="email"
              placeholder="Current Email"
              onChange={(e) => {
                handleChange(e);
              }}
            />
            <StyledText
              className="mt-4"
              fontSize="14px"
              fontWeight={600}
              textColor={"var(--app-secondary-color)"}
              style={{ marginBottom: "10px" }}
            >
              New Email
            </StyledText>
            <FormikInputField
              name="newEmail"
              type="email"
              placeholder="New Email"
              onChange={(e) => {
                handleChange(e);
              }}
            />
            {dirty && (
              <div className="d-flex justify-content-end gap-2 mt-4 pb-2">
                <Button
                  btnBg="#ffffff"
                  color="rgba(88, 85, 88, 1)"
                  borderColor="rgba(186, 189, 193, 1)"
                  $variant="customButton"
                  onClick={() => {
                    handleReset();
                  }}
                >
                  <CrossIcon size={20} /> Close
                </Button>
                <Button
                  btnBg={"var(--app-primary-color)"}
                  color={"var(--app-text-color)"}
                  // borderColor='rgba(186, 189, 193, 1)'
                  disabled={isUpdating || !isValid}
                  $variant="customButton"
                >
                  {isUpdating ? (
                    <span>Updating...</span>
                  ) : (
                    <>
                      <SaveIcon size={20} color="var(--app-text-color)" />{" "}
                      Update Email
                    </>
                  )}
                </Button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </CardContainer>
  );
};

export default ChangeLoginEmail;
